// Who owns or manages what, as the roles panel needs it (§4.7, migration 024).
//
// Pure, and separate from the panel, for this repository's standing reason: there is
// no DOM harness, so a decision left inside a component is a decision nothing can
// check. Three of the decisions here are the kind that fail quietly:
//
//   * THE DEFAULT SCOPE. Recording a role is something an agent does mid-call, having
//     just been told "I'm the manager here now". Almost always "here" is the store
//     already on the record in front of them, so the form opens pointed at it and the
//     common case is one click. Getting this wrong does not error — it just makes
//     every agent pick their way through three controls to say the obvious thing.
//   * THE EMAIL LIST. This feature exists to produce one. A list that silently
//     contains `undefined`, a duplicate, or an address that hard-bounced is worse than
//     no list, because it is pasted into a real send and nobody re-reads it.
//   * WHAT COUNTS AS THE SAME ROLE. The server answers a duplicate with 200 rather
//     than a conflict, so the panel must not present "already true" as a failure.

/** Wire value to what an agent reads. The server's enum is the source of truth. */
export const ROLE_LABELS = Object.freeze({
  store_manager: 'Store manager',
  store_owner: 'Store owner',
  brand_manager: 'Brand manager',
});

/** Display order — narrowest scope first, which is also how they are most often set. */
export const ROLE_ORDER = Object.freeze(['store_manager', 'store_owner', 'brand_manager']);

export const SCOPE_LABELS = Object.freeze({
  location: 'Store',
  organization: 'Organization',
  brand: 'Brand',
});

export const roleLabel = (role) => ROLE_LABELS[role] ?? role;

/**
 * Where the add form should point when it opens.
 *
 * The contact's own account, narrowest thing first: their store if they have one,
 * otherwise their organization, otherwise their brand, otherwise nothing and the
 * agent picks. Returns `{scope, scopeId, scopeName}` or null.
 */
export function defaultScope(customer) {
  const organization = customer?.organization ?? null;
  const location = customer?.location ?? null;
  if (location?.id) {
    return { scope: 'location', scopeId: String(location.id), scopeName: location.name ?? '' };
  }
  if (organization?.id) {
    return {
      scope: 'organization',
      scopeId: String(organization.id),
      scopeName: organization.name ?? '',
    };
  }
  if (organization?.brand?.id) {
    return {
      scope: 'brand',
      scopeId: String(organization.brand.id),
      scopeName: organization.brand.name ?? '',
    };
  }
  return null;
}

/**
 * The POST body, or null when the draft is not yet answerable.
 *
 * Null rather than a partial body: `POST /customers/:id/roles` requires all three, and
 * sending an incomplete one to find that out is a round trip that tells the agent
 * nothing they could not have been told without it.
 */
export function newRoleBody(draft) {
  const role = draft?.role;
  const scope = draft?.scope;
  const scopeId = draft?.scopeId;
  if (!ROLE_ORDER.includes(role)) return null;
  if (!Object.prototype.hasOwnProperty.call(SCOPE_LABELS, scope)) return null;
  if (!scopeId && scopeId !== 0) return null;
  return { role, scope, scope_id: String(scopeId) };
}

/**
 * Whether this contact already holds exactly this role over exactly this thing.
 *
 * Used to label the button rather than to block the save. The server treats a repeat
 * as already-true and answers 200, which is the right behaviour for two agents
 * recording the same fact on the same afternoon — so this only spares the agent a
 * request whose outcome is "nothing happened".
 */
export function alreadyHeld(roles, draft) {
  const body = newRoleBody(draft);
  if (!body) return false;
  return (roles ?? []).some(
    (held) => held.role === body.role
      && held.scope === body.scope
      && String(held.scope_id) === body.scope_id,
  );
}

/**
 * One readable line per role, sorted the way the panel renders them.
 *
 * `scope_name` can be null when the scope row was deleted out from under the role, so
 * it degrades to the id rather than rendering "Store manager of" and stopping.
 */
export function describeRoles(roles) {
  return [...(roles ?? [])]
    .sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role)
      || String(a.scope_name ?? '').localeCompare(String(b.scope_name ?? '')))
    .map((held) => ({
      ...held,
      label: roleLabel(held.role),
      scopeLabel: SCOPE_LABELS[held.scope] ?? held.scope,
      scopeName: held.scope_name ?? `#${held.scope_id}`,
    }));
}

/**
 * The addresses a send list actually consists of.
 *
 * Case-insensitively deduplicated, because the same person can hold the same role over
 * two stores and appear twice; blanks dropped, because migration 018 made
 * `customers.email` nullable and a phone-only contact is ordinary here, not an error.
 * Order is preserved so the list reads in the same order as the table above it.
 */
export function emailList(people) {
  const seen = new Set();
  const out = [];
  for (const person of people ?? []) {
    const email = String(person?.email ?? '').trim();
    if (!email) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(email);
  }
  return out;
}

/**
 * How many of the people found cannot actually be emailed.
 *
 * Shown beside the list because it is the difference between "12 brand managers" and
 * "12 brand managers, 9 of whom you can reach" — and the second number is the one that
 * matters when the point of the screen is a send.
 */
export function unreachableCount(people) {
  return (people ?? []).filter((person) => !String(person?.email ?? '').trim()).length;
}
